/**
 * Loop guard for automatic Azure AD sign-in attempts.
 *
 * The api-client interceptors and the _app.tsx session-error effect can auto-
 * trigger `signIn('azure-ad')`. If re-auth can't fix the underlying failure
 * (e.g. the backend rejects every token), this prevents an infinite redirect
 * loop: `shouldAttemptSignIn()` returns true at most once per window, and
 * callers surface an error (route to /auth/error) when it returns false.
 *
 * The timestamp is persisted to `sessionStorage` so the guard survives the
 * full-page OAuth redirects, falling back to an in-memory copy on the server or
 * when sessionStorage is unavailable.
 */

export const SIGN_IN_LOOP_GUARD_WINDOW_MS = 30_000

const STORAGE_KEY = 'AUTH_LAST_SIGN_IN_ATTEMPT_AT'

let lastSignInAttemptAt = 0

function readLastAttempt(): number {
  if (typeof window === 'undefined') return lastSignInAttemptAt
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY)
    const parsed = stored === null ? NaN : Number(stored)
    return Number.isFinite(parsed) ? parsed : lastSignInAttemptAt
  } catch {
    return lastSignInAttemptAt
  }
}

function writeLastAttempt(value: number): void {
  lastSignInAttemptAt = value
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(value))
  } catch {
    // sessionStorage unavailable; the in-memory value still guards this tab.
  }
}

/**
 * Returns true if a sign-in attempt should be triggered now, false if one was
 * triggered too recently. When it returns true it also records the attempt,
 * so it must only be called when the caller actually intends to invoke
 * `signIn(...)`.
 */
export function shouldAttemptSignIn(now: number = Date.now()): boolean {
  if (now - readLastAttempt() < SIGN_IN_LOOP_GUARD_WINDOW_MS) {
    return false
  }
  writeLastAttempt(now)
  return true
}

/**
 * Clears the guard. Used by tests and by the error page's manual retry.
 */
export function resetSignInGuard(): void {
  lastSignInAttemptAt = 0
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to clear if sessionStorage is unavailable.
  }
}
